import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const API_BASE = '/api/v1/user/collections';

export interface Collection {
  id: string;
  name: string;
  description: string | null;
  isPublic: boolean;
  shareToken: string | null;
  createdAt: string;
  updatedAt: string;
  bookmarkCount: number;
}

interface CollectionWithBookmarks extends Collection {
  bookmarks: Array<{
    id: string;
    judgmentId: number;
    notes: string | null;
    createdAt: string;
    judgment: {
      id: number;
      caseNumbers: string[];
      courtType: string;
      judgmentDate: string | null;
      summary: string | null;
    };
  }>;
}

export function useCollections() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    };
  }, []);

  const fetchCollections = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(API_BASE, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(t('collections.fetchError'));
      }

      const data = await response.json();
      setCollections(data.collections);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, t]);

  const createCollection = useCallback(
    async (name: string, description?: string): Promise<Collection | null> => {
      if (!isAuthenticated) return null;

      try {
        const response = await fetch(API_BASE, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ name, description }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || t('collections.createError'));
        }

        const newCollection = await response.json();
        // Ensure bookmarkCount is set (new collections have 0 bookmarks)
        const collectionWithCount = { ...newCollection, bookmarkCount: newCollection.bookmarkCount ?? 0 };
        setCollections((prev) => [...prev, collectionWithCount]);
        return collectionWithCount;
      } catch (err) {
        setError((err as Error).message);
        return null;
      }
    },
    [isAuthenticated, getAuthHeaders, t]
  );

  const updateCollection = useCallback(
    async (
      id: string,
      updates: { name?: string; description?: string }
    ): Promise<Collection | null> => {
      if (!isAuthenticated) return null;

      try {
        const response = await fetch(`${API_BASE}/${id}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(updates),
        });

        if (!response.ok) {
          throw new Error(t('collections.updateError'));
        }

        const updatedCollection = await response.json();
        // Preserve bookmarkCount from the existing collection if not returned
        setCollections((prev) =>
          prev.map((c) => (c.id === id ? { ...updatedCollection, bookmarkCount: updatedCollection.bookmarkCount ?? c.bookmarkCount } : c))
        );
        return updatedCollection;
      } catch (err) {
        setError((err as Error).message);
        return null;
      }
    },
    [isAuthenticated, getAuthHeaders, t]
  );

  const deleteCollection = useCallback(
    async (id: string): Promise<boolean> => {
      if (!isAuthenticated) return false;

      try {
        const response = await fetch(`${API_BASE}/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(t('collections.deleteError'));
        }

        setCollections((prev) => prev.filter((c) => c.id !== id));
        return true;
      } catch (err) {
        setError((err as Error).message);
        return false;
      }
    },
    [isAuthenticated, getAuthHeaders, t]
  );

  const getCollectionWithBookmarks = useCallback(
    async (id: string): Promise<CollectionWithBookmarks | null> => {
      if (!isAuthenticated) return null;

      try {
        const response = await fetch(`${API_BASE}/${id}`, {
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(t('collections.getError'));
        }

        const data = await response.json();
        return data.collection;
      } catch (err) {
        setError((err as Error).message);
        return null;
      }
    },
    [isAuthenticated, getAuthHeaders, t]
  );

  const shareCollection = useCallback(
    async (id: string): Promise<{ shareToken: string; shareUrl: string } | null> => {
      if (!isAuthenticated) return null;

      try {
        const response = await fetch(`${API_BASE}/${id}/share`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(t('collections.shareError'));
        }

        const data = await response.json();
        setCollections((prev) =>
          prev.map((c) =>
            c.id === id ? { ...c, isPublic: true, shareToken: data.shareToken } : c
          )
        );
        return { shareToken: data.shareToken, shareUrl: data.shareUrl };
      } catch (err) {
        setError((err as Error).message);
        return null;
      }
    },
    [isAuthenticated, getAuthHeaders, t]
  );

  const unshareCollection = useCallback(
    async (id: string): Promise<boolean> => {
      if (!isAuthenticated) return false;

      try {
        const response = await fetch(`${API_BASE}/${id}/share`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(t('collections.unshareError'));
        }

        setCollections((prev) =>
          prev.map((c) =>
            c.id === id ? { ...c, isPublic: false, shareToken: null } : c
          )
        );
        return true;
      } catch (err) {
        setError((err as Error).message);
        return false;
      }
    },
    [isAuthenticated, getAuthHeaders, t]
  );

  const moveBookmarkToCollection = useCallback(
    async (collectionId: string | null, bookmarkId: string): Promise<boolean> => {
      if (!isAuthenticated) return false;

      try {
        // If moving to uncategorized (null), use the bookmark update endpoint
        if (collectionId === null) {
          const response = await fetch(`/api/v1/user/bookmarks/${bookmarkId}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ collectionId: null }),
          });
          return response.ok;
        }

        const response = await fetch(`${API_BASE}/${collectionId}/move-bookmark`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ bookmarkId }),
        });

        if (!response.ok) {
          throw new Error(t('collections.moveError'));
        }

        // Refresh collections to update bookmark counts
        await fetchCollections();
        return true;
      } catch (err) {
        setError((err as Error).message);
        return false;
      }
    },
    [isAuthenticated, getAuthHeaders, fetchCollections, t]
  );

  // Load collections on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCollections();
    } else {
      setCollections([]);
    }
  }, [isAuthenticated, fetchCollections]);

  return {
    collections,
    isLoading,
    error,
    fetchCollections,
    createCollection,
    updateCollection,
    deleteCollection,
    getCollectionWithBookmarks,
    shareCollection,
    unshareCollection,
    moveBookmarkToCollection,
  };
}
